import { useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { fmtDateLong, groupByDate, addMinutesToTime } from "../lib/dates";
import { CalendarClock, Clock, Loader2, CalendarPlus, CalendarX2 } from "lucide-react";
import { toast } from "sonner";

const SPECIES = ["Perro", "Gato"];
const STUDIES = ["Ecografía abdominal", "Ecocardiografía", "Ecografía de gestación", "Intervencionismo", "Otra"];

const EMPTY = { patient_name: "", owner_name: "", species: "", age: "", weight: "", study_type: "", reason: "", notes: "" };

export function AdminBookingDialog({ clinics, slots, onCreated }) {
  const [open, setOpen] = useState(false);
  const [clinicId, setClinicId] = useState("");
  const [slotId, setSlotId] = useState("");
  const [form, setForm] = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);

  const grouped = useMemo(
    () => groupByDate((slots || []).filter((s) => s.status === "available")),
    [slots]
  );
  const selectedSlot = (slots || []).find((s) => s.id === slotId);

  const reset = () => { setClinicId(""); setSlotId(""); setForm({ ...EMPTY }); };

  const submit = async () => {
    if (!clinicId) { toast.error("Selecciona la clínica"); return; }
    if (!slotId) { toast.error("Selecciona una franja disponible"); return; }
    if (!form.patient_name || !form.species || !form.study_type) {
      toast.error("Completa el paciente, la especie y el tipo de estudio"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/bookings", { clinic_id: clinicId, slot_id: slotId, ...form });
      toast.success("Reserva creada y confirmada");
      setOpen(false); reset(); onCreated();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-[#373E45] hover:bg-[#2b3137] text-white font-bold rounded-xl" data-testid="admin-create-booking-btn">
          <CalendarPlus className="h-4 w-4 mr-1.5" />Crear reserva para una clínica
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-booking-dialog">
        <DialogHeader>
          <DialogTitle>Crear reserva para una clínica</DialogTitle>
          <DialogDescription>Para reservas hechas fuera de la web. Se creará como confirmada y aparecerá en el área de la clínica.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-[#373E45] font-semibold">Clínica</Label>
            <Select value={clinicId} onValueChange={setClinicId}>
              <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="admin-booking-clinic-select"><SelectValue placeholder="Selecciona la clínica" /></SelectTrigger>
              <SelectContent>
                {clinics.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`admin-booking-clinic-${c.id}`}>{c.clinic_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[#373E45] font-semibold">Franja disponible</Label>
            {grouped.length === 0 ? (
              <div className="mt-2 flex items-center gap-2 text-sm text-[#64748B] bg-slate-50 rounded-xl p-3"><CalendarX2 className="h-4 w-4" />No hay franjas libres. Crea disponibilidad primero.</div>
            ) : (
              <div className="mt-1.5 max-h-[220px] overflow-y-auto space-y-3 border border-slate-200 rounded-xl p-3" data-testid="admin-booking-slots">
                {grouped.map(({ date, slots: daySlots }) => (
                  <div key={date}>
                    <p className="font-bold text-xs text-[#373E45] capitalize mb-1.5 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-[#7FD498]" />{fmtDateLong(date)}</p>
                    <div className="flex flex-wrap gap-2">
                      {daySlots.map((s) => {
                        const active = slotId === s.id;
                        return (
                          <button key={s.id} type="button" onClick={() => setSlotId(s.id)} data-testid={`admin-booking-slot-${s.id}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border font-bold text-sm transition-colors ${active ? "bg-[#7FD498] border-[#7FD498] text-[#373E45]" : "border-[#7FD498] bg-[#7FD498]/10 text-[#373E45] hover:bg-[#7FD498]/30"}`}>
                            <Clock className="h-3.5 w-3.5" />{s.start_time}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedSlot && (
              <p className="text-xs text-[#64748B] mt-1.5">Cita: {fmtDateLong(selectedSlot.date)} · {selectedSlot.start_time} – {addMinutesToTime(selectedSlot.start_time, 90)} (1 h 30 min)</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#373E45] font-semibold">Nombre del paciente</Label>
              <Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="Ej. Luna" data-testid="admin-booking-patient-input" />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Propietario</Label>
              <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="Ej. María García" data-testid="admin-booking-owner-input" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[#373E45] font-semibold">Especie</Label>
              <Select value={form.species} onValueChange={(v) => setForm({ ...form, species: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="admin-booking-species-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{SPECIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Edad</Label>
              <Input value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="3 años" data-testid="admin-booking-age-input" />
            </div>
            <div>
              <Label className="text-[#373E45] font-semibold">Peso</Label>
              <Input value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="12 kg" data-testid="admin-booking-weight-input" />
            </div>
          </div>

          <div>
            <Label className="text-[#373E45] font-semibold">Tipo de estudio</Label>
            <Select value={form.study_type} onValueChange={(v) => setForm({ ...form, study_type: v })}>
              <SelectTrigger className="mt-1.5 bg-slate-50" data-testid="admin-booking-study-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>{STUDIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[#373E45] font-semibold">Motivo / sospecha clínica</Label>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 bg-slate-50" placeholder="Opcional" data-testid="admin-booking-reason-input" />
          </div>
          <div>
            <Label className="text-[#373E45] font-semibold">Comentarios</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5 bg-slate-50 resize-none" placeholder="Opcional" data-testid="admin-booking-notes-input" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" className="rounded-xl" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={submitting} className="bg-[#7FD498] hover:bg-[#6BC284] text-[#373E45] font-bold rounded-xl" data-testid="admin-booking-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear reserva"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
